import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import type { Setting } from "@/lib/settings.api";
import { humanizeKey } from "../../settingsPage.helpers";
import { IMPACT_STYLES, SETTING_COPY } from "../systemSettings.constants";

interface SettingControlProps {
  setting: Setting;
  value: string;
  dirty: boolean;
  saving: boolean;
  onChange: (value: string) => void;
  onSave: () => void | Promise<void>;
}

export function SettingControl({
  setting,
  value,
  dirty,
  saving,
  onChange,
  onSave,
}: SettingControlProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const copy = SETTING_COPY[setting.key] ?? {
    label: humanizeKey(setting.key),
    helper: setting.description ?? "Paramètre système.",
    impact: "normal" as const,
  };
  const impact = IMPACT_STYLES[copy.impact];
  const ImpactIcon = impact.icon;

  return (
    <div className={`flex min-h-full flex-col border bg-card p-3 ${impact.cardClassName}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-foreground">{copy.label}</p>
          <span
            className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] ${impact.className}`}
          >
            <ImpactIcon size={12} />
            {impact.label}
          </span>
        </div>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{copy.helper}</p>
        <p className="mt-1 text-[11px] text-subtle">Identifiant interne : {setting.key}</p>
      </div>

      <div className={`my-3 border-t border-dashed ${impact.ruleClassName}`} />

      <div className="mt-auto space-y-3">
        {setting.type === "boolean" ? (
          <div className="flex items-center justify-between border border-border bg-card px-3 py-2">
            <span className="text-xs text-body">{value === "true" ? "Activé" : "Désactivé"}</span>
            <Switch
              checked={value === "true"}
              onCheckedChange={(checked) => onChange(String(checked))}
            />
          </div>
        ) : copy.options ? (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="h-10 bg-card">
              <SelectValue placeholder="Choisir une règle" />
            </SelectTrigger>
            <SelectContent>
              {copy.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              type={
                setting.type === "integer"
                  ? "number"
                  : setting.key === "mail_sender_email"
                    ? "email"
                    : "text"
              }
              value={value}
              onChange={(event) => onChange(event.target.value)}
              className="h-10 bg-card"
            />
            {copy.unit && <span className="w-20 text-xs text-muted-foreground">{copy.unit}</span>}
          </div>
        )}
        {copy.options && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            {copy.options.find((option) => option.value === value)?.detail}
          </p>
        )}

        <Button
          size="sm"
          variant={dirty ? "default" : "secondary"}
          disabled={!dirty || saving}
          onClick={() => setConfirmOpen(true)}
          className="w-full"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Enregistrer
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Confirmer la modification"
        description={`Vous êtes sur le point de modifier « ${copy.label} ». Cette valeur affecte le comportement de l'application. Confirmez-vous l'enregistrement ?`}
        confirmLabel="Enregistrer"
        loading={saving}
        onConfirm={() => {
          Promise.resolve(onSave()).finally(() => setConfirmOpen(false));
        }}
      />
    </div>
  );
}
